/** @jsx jsx */
import * as React from 'react';
import { jsx, css } from '@emotion/core';
import cx from 'classnames';
import {
  Menu,
  MenuItem as RKMenuItem,
  MenuItemProps as RKMenuItemProps,
  MenuSeparator,
  MenuInitialState,
  useMenuState,
} from 'reakit/Menu';

import { forwardRefWithAs, ComponentWithAs } from '../../common/types';

export interface CKActionMenuComposition {
  Item: ComponentWithAs<'button', CKActionMenuItemProps>;
  Divider: React.FC;
}

export interface CKActionMenuProps {
  className?: string;
  options?: MenuInitialState;
}

export interface CKActionMenuItemProps extends Partial<RKMenuItemProps> {
  children: React.ReactNode;
  isDestructive?: boolean;
  icon?: React.ReactNode;
  isDisabled?: boolean;
}

export const CKActionMenu: React.FC<CKActionMenuProps> &
  CKActionMenuComposition = ({ children, options, className, ...rest }) => {
  const menuState = useMenuState(options);
  const menuClass = cx('ck-popover-menu', className);

  return (
    <Menu
      className={menuClass}
      css={css`
        outline: 0;
        &:focus {
          outline: 0;
        }
      `}
      {...menuState}
      {...rest}
      visible
    >
      {React.Children.map(children, child => {
        // @ts-ignore
        return React.cloneElement(child, menuState);
      })}
    </Menu>
  );
};

const MenuItemInner: React.FC<Pick<CKActionMenuItemProps, 'icon'>> = ({
  children,
  icon,
}) => {
  return (
    <React.Fragment>
      {icon && <span className="ck-popover-menu-item-icon">{icon}</span>}
      <span>{children}</span>
    </React.Fragment>
  );
};

export const CKActionMenuItem = forwardRefWithAs<
  CKActionMenuItemProps,
  'button'
>((props, ref) => {
  const { children, icon, isDestructive, isDisabled, ...rest } = props;

  const itemClass = cx('ck-popover-menu-item', {
    'ck-popover-menu-item--destroy': isDestructive,
  });

  return (
    // @ts-ignore
    <RKMenuItem ref={ref} disabled={isDisabled} className={itemClass} {...rest}>
      <MenuItemInner children={children} icon={icon} />
    </RKMenuItem>
  );
});

const MenuDivider: React.FC = props => (
  <MenuSeparator className="ck-popover-divider" {...props} />
);

CKActionMenu.Item = CKActionMenuItem;
CKActionMenu.Divider = MenuDivider;
